import { z } from 'zod';
/**
 * LINE Pay API Base URL 驗證
 */
const linePayApiBaseSchema = z
    .string()
    .refine(val => val === 'https://sandbox-api-pay.line.me' ||
    val === 'https://api-pay.line.me', {
    message: 'LINE Pay API Base 必須是 https://sandbox-api-pay.line.me 或 https://api-pay.line.me',
})
    .nullable()
    .optional();
/**
 * URL 驗證 Schema
 */
const urlSchema = z
    .string()
    .url({ message: '必須是有效的 URL' })
    .nullable()
    .optional();
/**
 * 更新商家請求 Schema
 * 此 schema 供前後端共用，用於驗證商家更新請求
 */
export const UpdateTenantRequestSchema = z.object({
    name: z.string().min(1).max(100).optional().describe('商家名稱'),
    phone: z
        .union([
        z.string().regex(/^09\d{8}$/, '請輸入有效的台灣手機號碼'),
        z.literal(''),
        z.literal(null),
    ])
        .optional()
        .describe('商家電話（格式：09XXXXXXXX）'),
    logo_url: z.string().url().nullable().optional().describe('Logo 圖片 URL'),
    logo_dark_url: z
        .string()
        .url()
        .nullable()
        .optional()
        .describe('深色模式 Logo 圖片 URL'),
    favicon_url: z
        .string()
        .url()
        .nullable()
        .optional()
        .describe('Favicon 圖片 URL'),
    cover_image_url: z
        .string()
        .url()
        .nullable()
        .optional()
        .describe('封面圖片 URL'),
    avatar_url: z.string().url().nullable().optional().describe('頭像圖片 URL'),
    // LINE Pay 設定
    linepay_channel_id: z
        .string()
        .min(1)
        .max(100)
        .nullable()
        .optional()
        .describe('LINE Pay Channel ID'),
    linepay_channel_secret: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('LINE Pay Channel Secret（會加密儲存）'),
    // 注意：LINE Pay API Base URL 現在一律由環境變數決定，不再由每個商家自行設定
    linepay_return_url: urlSchema
        .optional()
        .describe('LINE Pay 支付完成回調 URL'),
    linepay_cancel_url: urlSchema
        .optional()
        .describe('LINE Pay 支付取消回調 URL'),
    linepay_is_enabled: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否啟用商家專屬 LINE Pay (0: 使用全域設定, 1: 使用商家設定)'),
    // 綠界金流設定
    ecpay_payment_merchant_id: z
        .string()
        .min(1)
        .max(100)
        .nullable()
        .optional()
        .describe('綠界金流商店代號'),
    ecpay_payment_hash_key: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('綠界金流 HashKey（會加密儲存）'),
    ecpay_payment_hash_iv: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('綠界金流 HashIV（會加密儲存）'),
    ecpay_payment_api_base: z
        .string()
        .url()
        .nullable()
        .optional()
        .describe('綠界金流 API Base URL'),
    ecpay_payment_return_url: urlSchema
        .optional()
        .describe('綠界金流付款完成返回 URL'),
    ecpay_payment_order_result_url: urlSchema
        .optional()
        .describe('綠界金流付款結果通知 URL（Webhook）'),
    ecpay_payment_is_enabled: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否啟用綠界金流 (0: 停用, 1: 啟用)'),
    /** 綠界啟用的付款方式清單（結帳頁僅顯示此清單內按鈕）；空或未設則等同 ALL */
    ecpay_choose_payment: z
        .array(z.string())
        .optional()
        .describe('綠界啟用付款方式（ApplePay、LinePay、TWQR、GooglePay、Credit、ATM、CVS、BARCODE）'),
    // 綠界發票設定
    ecpay_invoice_merchant_id: z
        .string()
        .min(1)
        .max(100)
        .nullable()
        .optional()
        .describe('綠界發票商店代號'),
    ecpay_invoice_hash_key: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('綠界發票 HashKey（會加密儲存）'),
    ecpay_invoice_hash_iv: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('綠界發票 HashIV（會加密儲存）'),
    ecpay_invoice_api_base: z
        .string()
        .url()
        .nullable()
        .optional()
        .describe('綠界發票 API Base URL'),
    ecpay_invoice_is_enabled: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否啟用綠界發票 (0: 停用, 1: 啟用)'),
    // 綠界物流設定
    ecpay_logistics_merchant_id: z
        .string()
        .min(1)
        .max(100)
        .nullable()
        .optional()
        .describe('綠界物流商店代號'),
    ecpay_logistics_hash_key: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('綠界物流 HashKey（會加密儲存）'),
    ecpay_logistics_hash_iv: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('綠界物流 HashIV（會加密儲存）'),
    ecpay_logistics_api_base: z
        .string()
        .url()
        .nullable()
        .optional()
        .describe('綠界物流 API Base URL'),
    ecpay_logistics_server_reply_url: z
        .string()
        .url()
        .nullable()
        .optional()
        .describe('綠界物流狀態回呼 URL（ServerReplyURL）'),
    ecpay_logistics_is_enabled: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否啟用綠界物流 (0: 停用, 1: 啟用)'),
    /** 啟用的取貨／物流方式（結帳頁僅顯示此清單內門市與宅配）；空或未設則全部顯示 */
    delivery_methods_enabled: z
        .array(z.string())
        .optional()
        .describe('啟用物流／取貨方式（7-ELEVEN、FamilyMart、OK_MART、HiLife、HOME_DELIVERY）'),
    // LINE 設定（表單填寫方式）
    line_basic_id: z
        .string()
        .min(1)
        .max(100)
        .nullable()
        .optional()
        .describe('LINE Basic ID（例如: @burger_shop）'),
    line_channel_id: z
        .string()
        .min(1)
        .max(100)
        .nullable()
        .optional()
        .describe('LINE Channel ID'),
    line_channel_secret: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('LINE Channel Secret（會加密儲存）'),
    // Chatbot 平台啟用狀態
    chatbot_line_enabled: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否啟用 LINE Chatbot 平台 (0: 停用, 1: 啟用)'),
    chatbot_web_widget_enabled: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否啟用 Web widget Chatbot 平台 (0: 停用, 1: 啟用)'),
    chatbot_fb_ig_enabled: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否啟用 FB/IG Chatbot 平台 (0: 停用, 1: 啟用)'),
    chatbot_telegram_enabled: z
        .number()
        .int()
        .min(0)
        .max(1)
        .optional()
        .describe('是否啟用 Telegram Chatbot 平台 (0: 停用, 1: 啟用)'),
    // Web widget 平台設定
    web_widget_api_key: z
        .string()
        .min(1)
        .max(100)
        .nullable()
        .optional()
        .describe('Web widget API Key'),
    web_widget_secret: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('Web widget Secret（會加密儲存）'),
    web_widget_domain: z
        .string()
        .max(500)
        .nullable()
        .optional()
        .describe('Web widget 允許的網域（逗號分隔）'),
    // FB/IG 平台設定
    fb_ig_app_id: z
        .string()
        .min(1)
        .max(100)
        .nullable()
        .optional()
        .describe('Facebook App ID'),
    fb_ig_app_secret: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('Facebook App Secret（會加密儲存）'),
    fb_ig_page_access_token: z
        .string()
        .min(1)
        .max(500)
        .nullable()
        .optional()
        .describe('Facebook Page Access Token（會加密儲存）'),
    fb_ig_page_id: z
        .string()
        .min(1)
        .max(100)
        .nullable()
        .optional()
        .describe('Facebook Page ID'),
    // Telegram 平台設定
    telegram_bot_token: z
        .string()
        .min(1)
        .max(200)
        .nullable()
        .optional()
        .describe('Telegram Bot Token（會加密儲存）'),
    telegram_bot_username: z
        .string()
        .min(1)
        .max(100)
        .nullable()
        .optional()
        .describe('Telegram Bot Username（例如: @my_bot）'),
    telegram_webhook_url: urlSchema.optional().describe('Telegram Webhook URL'),
    // 稅率設定
    tax_rate_bps: z
        .number()
        .int()
        .min(0)
        .max(10000)
        .optional()
        .describe('稅率（basis points），500 代表 5%，預設值 500，範圍 0-10000 (0%-100%)'),
    // 點數兌換規則
    points_per_yuan: z
        .number()
        .int()
        .min(1)
        .max(10000)
        .optional()
        .describe('點數兌換規則（多少點折1元），預設值 20，範圍 1-10000'),
    /** 面交預購付款模式：online=線上付款（預設）、pickup=面交取貨時付款 */
    preorder_payment_mode: z
        .enum(['online', 'pickup'])
        .optional()
        .describe('面交預購付款模式'),
});
